import classNames from "classnames";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import PrevIcon from "@mui/icons-material/ChevronLeft";
import NextIcon from "@mui/icons-material/ChevronRight";

import PropTypes from "prop-types";
import React from "react";

import { preventDefault } from "lib/util";

const SimplePaginationControl = ({ numPages, currentPage, onPageSelect, range }) => {
    const lastIdx = numPages + 1; // position after last possible page, makes math easier
    // keeps range of pages displayed consistent
    const startPage = Math.max(
        1, // min start page
        Math.min(
            currentPage - Math.floor(range / 2), // should start at current - 1/2 range
            lastIdx - range, // max start page to maintain window
        ),
    );
    const endIdx = Math.min(lastIdx, startPage + range); // end page index

    // make a quick array of linkable page numbers to map over
    const linkedPages = Array.from({ length: endIdx - startPage }, (_, i) => i + startPage);

    // generalize actual selector link
    const pageSelector = (value, displayText, key, selected = false) => (
        <a
            href="#"
            onClick={preventDefault(() => onPageSelect(value))}
            className={classNames("pagination-control__selector text-caption", {
                "pagination-control__selector--active": selected,
                disabled: currentPage === value && !selected,
            })}
            key={key}
        >
            {displayText || value}
        </a>
    );

    // navigation links
    const firstPage = pageSelector(1, <FirstPageIcon />);
    const prevPage = pageSelector(Math.max(1, currentPage - 1), <PrevIcon />);
    const nextPage = pageSelector(Math.min(numPages, currentPage + 1), <NextIcon />);
    const lastPage = pageSelector(numPages, <LastPageIcon />);

    // interface
    return (
        <div className="pagination-control__container">
            {firstPage}
            {prevPage}

            <span className="pagination-control__spacer">
                {startPage > 1 ? <>&hellip;</> : <>&nbsp;</>}
            </span>

            {linkedPages.map((pageNum) =>
                pageSelector(pageNum, pageNum, pageNum, currentPage === pageNum),
            )}

            <span className="pagination-control__spacer">
                {endIdx < lastIdx ? <>&hellip;</> : <>&nbsp;</>}
            </span>

            {nextPage}
            {lastPage}
        </div>
    );
};

SimplePaginationControl.propTypes = {
    numPages: PropTypes.number.isRequired,
    currentPage: PropTypes.number,
    onPageSelect: PropTypes.func.isRequired,
    range: PropTypes.number,
};

SimplePaginationControl.defaultProps = {
    currentPage: 1,
    range: 5, // prefer odd numbers, to keep things centered, but not required
};

export default React.memo(SimplePaginationControl);
